import { useState, useEffect, useRef } from 'react'

type GotData<Output> = { status: 'data'; output: Output }
type NoInput = { status: 'noInput'; output: undefined }
type Loading = { status: 'loading'; output: undefined }

type UseDataFetchReturns<Input, Output> = (
  | GotData<Output>
  | NoInput
  | Loading
) & { setInput: (uinput: Input) => void }

// Race condition avoidance technique borrowed from:
// https://medium.com/hackernoon/avoiding-race-conditions-when-fetching-data-with-react-hooks-220d6fd0f663
// If you change isCancelled = true -> isCancelled = false
// in the code below, you can see the race condition unit test
// fail

export default function useDataFetch<Input extends any, Output extends any>(
  initFetchData: (input: Input) => Promise<Output>,
  initErrHandler: (err: Error) => Output,
  initialInput?: Input
): UseDataFetchReturns<Input, Output> {
  const [input, setInput] = useState<Input | undefined>(initialInput)
  const [output, setOutput] = useState<Output>()
  const [isLoading, setIsLoading] = useState(false)
  const fetchData = useRef(initFetchData)
  const errHandler = useRef(initErrHandler)
  useEffect(() => {
    if (input === undefined) {
      return () => null
    }
    let isCancelled = false
    const cancel = (): void => {
      isCancelled = true
    }
    setIsLoading(true)
    const update = async (): Promise<void> => {
      const response = await fetchData.current(input).catch(errHandler.current)
      if (!isCancelled) {
        setOutput(response)
        setIsLoading(false)
      }
    }
    update()
    return cancel
  }, [input, setOutput, fetchData, errHandler])

  if (isLoading) {
    return { output: undefined, status: 'loading', setInput }
  } else if (output) {
    return { output, status: 'data', setInput }
  } else {
    return { output: undefined, status: 'noInput', setInput }
  }
}
